import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Facebook } from "lucide-react";
import Logo from "./Logo";
import { WhatsAppIcon } from "./FloatingWhatsApp";
import { storeConfig, telHref } from "../../config/store.config";
import { whatsappChatUrl } from "../../lib/whatsapp";

const shopLinks = [
  { to: "/shop", label: "Shop All" },
  { to: "/collections", label: "Collections" },
  { to: "/shop?collection=oversized", label: "Oversized" },
  { to: "/shop?collection=essentials", label: "Essentials" },
  { to: "/shop?collection=graphics", label: "Graphics" },
];
const infoLinks = [
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/faq", label: "FAQ" },
  { to: "/size-guide", label: "Size Guide" },
  { to: "/shipping-policy", label: "Shipping" },
];
const legalLinks = [
  { to: "/return-policy", label: "Returns & Exchange" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms & Conditions" },
];

const Col = ({ title, links }) => (
  <div>
    <h4 className="label-mono text-brand mb-4">{title}</h4>
    <ul className="space-y-2.5">
      {links.map((l) => (
        <li key={l.to + l.label}>
          <Link
            to={l.to}
            className="text-sm text-paper/70 transition-colors hover:text-paper link-underline"
            data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <footer className="bg-ink text-paper" data-testid="site-footer">
    <div className="container-9one py-16">
      <div className="grid gap-12 lg:grid-cols-12">
        {/* Brand + address */}
        <div className="lg:col-span-4">
          <div className="inline-block border border-white/15 p-3">
            <Logo />
          </div>
          <p className="mt-6 flex items-start gap-2 text-sm text-paper/70">
            <MapPin size={16} className="mt-0.5 shrink-0 text-brand" />
            <span>
              {storeConfig.storeName}
              <br />
              {storeConfig.address}
              <br />
              {storeConfig.city} – {storeConfig.pincode}
              <br />
              {storeConfig.state}, {storeConfig.country}
            </span>
          </p>
          <div className="mt-5 space-y-1.5 text-sm">
            <p className="text-paper/50 label-mono">Primary</p>
            <a
              href={telHref(storeConfig.primaryPhone)}
              className="flex items-center gap-2 text-paper/80 hover:text-brand"
              data-testid="footer-primary-phone"
            >
              <Phone size={14} /> {storeConfig.primaryContactName} — {storeConfig.primaryPhone}
            </a>
            <a
              href={telHref(storeConfig.secondaryPhone)}
              className="flex items-center gap-2 text-paper/60 hover:text-brand"
              data-testid="footer-secondary-phone"
            >
              <Phone size={14} /> {storeConfig.secondaryContactName} — {storeConfig.secondaryPhone}
            </a>
          </div>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-6">
          <Col title="Shop" links={shopLinks} />
          <Col title="Info" links={infoLinks} />
          <Col title="Legal" links={legalLinks} />
        </div>

        {/* Social / contact */}
        <div className="lg:col-span-2">
          <h4 className="label-mono text-brand mb-4">Connect</h4>
          <div className="flex flex-col gap-3">
            <a
              href={whatsappChatUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-paper/70 hover:text-brand"
              data-testid="footer-whatsapp"
            >
              <WhatsAppIcon size={18} /> WhatsApp
            </a>
            {storeConfig.instagramUrl ? (
              <a
                href={storeConfig.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-paper/70 hover:text-brand"
                data-testid="footer-instagram"
              >
                <Instagram size={18} /> Instagram
              </a>
            ) : (
              <span className="flex items-center gap-2 text-sm text-paper/40" title="Instagram — coming soon">
                <Instagram size={18} /> Instagram
              </span>
            )}
            {storeConfig.facebookUrl && (
              <a
                href={storeConfig.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-paper/70 hover:text-brand"
                data-testid="footer-facebook"
              >
                <Facebook size={18} /> Facebook
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-paper/50 sm:flex-row sm:items-center">
        <p className="font-mono">
          © {new Date().getFullYear()} {storeConfig.storeName} · {storeConfig.domain}
        </p>
        <p className="font-mono">Made in {storeConfig.country}</p>
      </div>
    </div>
  </footer>
);

export default Footer;
